import { CoreImageView, CoreLayoutView, ORIENTATION_ENUM, TCoreLayoutOptions, type TCoreLayoutStyleProps } from '@core/widgets';
import { AssetsImages, TCommonData, TMargin, TViewCommonOption, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { ElementType, MouseEventHandler } from 'react';
import { getSemanticColors, SemanticColor, TColorVarsMap, toColorVars } from '../../theme/colors';
import { TVisualOption } from '../../types/options';
import { Badge } from '../UI/Badge/Badge';
import { FlagIcon } from '../UI/FlagIcon/FlagIcon';
import { getHref } from './imageUtils';
import styles from './styles/visual.module.css';
import {
    getVisualCircleMap,
    getVisualIconMap,
    HOVER_INSET,
    TVisualContainerPalette,
    TVisualHierarchy,
    TVisualIconPalette,
    TVisualVariant,
} from './styles/visualUtils';

/** Local variables of `visual.module.css`, filled from the semantic tokens of the widget's design version. */
const SELECTED_COLOR_VARS = {
    '--visual-selected-bg': SemanticColor.OverlayPrimary,
    '--visual-selected-border': SemanticColor.BorderVisualSelected,
} as const satisfies TColorVarsMap;

export type TVisualViewProps = {
    /** Design version of the widget showing the visual (`TWidget.version`): picks its colors and icon set. */
    version: number | undefined;
    uid?: TWidget<TCommonData>['uid'];
    widget?: TWidget<TCommonData>;
    iconName?: string;
    defaultIconName?: string;
    iconVariant?: 'simple' | 'circle';
    iconHierarchy?: TVisualOption['iconHierarchy'];
    size?: TVisualOption['size'];
    /** Explicit icon width in px. Takes priority over the asset's intrinsic width and the size-derived default. */
    iconWidth?: number;
    /** Explicit icon height in px. Takes priority over the asset's intrinsic height and the size-derived default. */
    iconHeight?: number;
    flagIcon?: string;
    flagIconSmall?: string;
    avatar?: string;
    base64?: string;
    rightMargin?: boolean;
    leftMargin?: boolean;
    margin?: TMargin;
    isSelected?: boolean;
    /** Overrides the hierarchy-derived icon color. Accepts any CSS color value (e.g. '#fff', 'var(--purple_100)'). */
    iconColor?: string;
    /** Overrides the hierarchy-derived background color (circle variant). Accepts any CSS color value. */
    backgroundColor?: string;
    handleClick?: MouseEventHandler;
    hovered?: TCoreLayoutStyleProps['hovered'];
    as?: ElementType;
    role?: React.AriaRole;
    styleName?: TViewCommonOption['styleName'];
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'parentStyleNames'> &
    Pick<TCoreLayoutOptions, 'opacity'>;

const getFlagSize = (size?: TVisualViewProps['size']) => {
    return size === 'medium' ? '80px' : '40px';
};

const getIconSize = <T,>(size?: T) => {
    switch (size) {
        case 'xxSmall':
            return 20;
        case 'medium':
            return 48;
        case 'large':
            return 80;
        default:
            return 24;
    }
};

const getIconWrapperSize = <T,>(size?: T) => {
    switch (size) {
        case 'xxSmall':
            return 20;
        case 'medium':
            return 80;
        case 'large':
            return 100;
        default:
            return 40;
    }
};

const getBase64 = (base64?: string) => {
    if (base64) {
        return base64.startsWith('data:image') ? base64 : 'data:image;base64,' + base64;
    }
    return undefined;
};

/**
 * Composable building block for rendering a visual element (icon, avatar, flag icon).
 * Owns only the rendering output; accepts pre-resolved behavior props (isDisabled, isHide)
 * from the parent, making it suitable for use inside complex widget trees where behavior
 * state is already managed one level up.
 *
 * Does not call useBehavior or any other behavior hook — those are the responsibility
 * of VisualWidget (server-driven) or the direct caller (composable usage).
 */
export const VisualView = ({
    version,
    uid,
    widget,
    iconName,
    defaultIconName,
    iconVariant,
    iconHierarchy,
    size,
    iconWidth,
    iconHeight,
    flagIcon,
    flagIconSmall,
    avatar,
    base64,
    rightMargin,
    margin,
    isDisabled = false,
    isHide,
    isSelected,
    iconColor,
    backgroundColor,
    handleClick,
    hovered,
    styleName,
    parentStyleNames,
    opacity,
    as,
    role = 'img',
}: TVisualViewProps) => {
    const base64ForImage = getBase64(base64);
    const iconImage = AssetsImages[iconName as keyof typeof AssetsImages];

    const containerMargin = rightMargin ? { ...(margin ?? {}), right: 16 } : margin;

    const getContainerSize = () => {
        if (base64ForImage || avatar) {
            const s = size === 'medium' ? 80 : 40;
            return { width: s, height: s };
        }
        if (iconName) {
            return {
                width: iconWidth ?? iconImage?.width ?? getIconWrapperSize(size),
                height: iconHeight ?? iconImage?.height ?? getIconWrapperSize(size),
            };
        }
        if (flagIcon) {
            const s = size === 'medium' ? 80 : 40;
            return { width: s, height: s };
        }
        return { width: 'auto', height: 'auto' };
    };

    const { width: containerWidth, height: containerHeight } = getContainerSize();

    const hierarchy: TVisualHierarchy = iconHierarchy ?? ('primary' as TVisualHierarchy);
    const variant: TVisualVariant = iconVariant === 'circle' ? 'circle' : 'simple';

    // The palettes below are handed to the core widgets as whole objects (base value + nested
    // `disabled` / `hovered` / `pressed` variants): resolving the active state is the core widget's job,
    // this view never branches on isDisabled or on hover.
    const hoveredPalette: TVisualContainerPalette['hovered'] = hovered && {
        boxShadow: hovered.backgroundColor ? `0 0 0 ${HOVER_INSET}px ${hovered.backgroundColor}` : undefined,
        ...hovered,
    };

    const semanticColors = getSemanticColors(version);
    const visualIconMap = getVisualIconMap(semanticColors);
    const circlePalette = variant === 'circle' ? getVisualCircleMap(semanticColors)[hierarchy] : undefined;
    const containerPalette: TVisualContainerPalette = {
        ...circlePalette,
        ...(backgroundColor ? { backgroundColor, disabled: { ...circlePalette?.disabled, backgroundColor } } : {}),
        hovered: hoveredPalette,
        pressed: hoveredPalette,
    };

    const iconPalette: TVisualIconPalette = iconColor ? { iconColor, disabled: { iconColor } } : visualIconMap[variant][hierarchy];

    const containerStyleProps: TCoreLayoutStyleProps = {
        orientation: ORIENTATION_ENUM.BOX,
        borderRadius: 1000,
        margin: containerMargin,
        width: containerWidth,
        flexShrink: 0,
        height: containerHeight,
        borderStroke: variant === 'circle' ? 1 : undefined,
        opacity,
        ...containerPalette,
    };

    const childParentStyleNames = styleName ? [...(parentStyleNames ?? []), styleName] : parentStyleNames;
    const layoutStyleName = 'visualLayout';
    const imageStyleName = 'visualImage';

    const getVisual = () => {
        if (base64ForImage || avatar) {
            const avatarSize = size === 'medium' ? 80 : 40;
            return (
                <CoreImageView
                    src={base64ForImage || avatar}
                    isDisabled={isDisabled}
                    parentStyleNames={childParentStyleNames}
                    styleName={imageStyleName}
                    defaultIcon={getHref(defaultIconName ?? '', version)}
                    styleProps={{
                        width: avatarSize,
                        height: avatarSize,
                        defaultIconHeight: 24,
                        defaultIconWidth: 24,
                        borderRadius: 1000,
                        overflow: 'hidden',
                        objectFit: 'cover',
                        ...iconPalette,
                    }}
                />
            );
        } else if (iconName || defaultIconName) {
            return (
                <CoreImageView
                    src={getHref(iconName, version)}
                    isDisabled={isDisabled}
                    parentStyleNames={childParentStyleNames}
                    styleName={imageStyleName}
                    defaultIcon={getHref(defaultIconName, version)}
                    styleProps={{
                        width: iconWidth ?? iconImage?.width ?? getIconSize(size),
                        height: iconHeight ?? iconImage?.height ?? getIconSize(size),
                        ...iconPalette,
                    }}
                />
            );
        } else if (flagIcon) {
            return <FlagIcon name={flagIcon} size={getFlagSize(size)} isDisabled={isDisabled} />;
        }
    };

    return (
        <CoreLayoutView
            uid={uid}
            widget={widget}
            as={as}
            role={role}
            isHide={isHide}
            isDisabled={isDisabled}
            styleProps={containerStyleProps}
            parentStyleNames={childParentStyleNames}
            styleName={layoutStyleName}
            onClick={handleClick}
        >
            {getVisual()}
            {flagIconSmall && (
                <span className={clsx(styles.badgeContainer, size === 'medium' && styles.medium)}>
                    <Badge flagIcon={flagIconSmall} size={size} parentStyleNames={childParentStyleNames} isDisabled={isDisabled} />
                </span>
            )}
            {isSelected && (
                <div className={styles.selectedContainer} style={toColorVars(SELECTED_COLOR_VARS, version)}>
                    <CoreImageView
                        src={getHref('ic_check', version)}
                        isDisabled={isDisabled}
                        parentStyleNames={childParentStyleNames}
                        styleName={imageStyleName}
                        styleProps={{ width: 24, height: 24, ...visualIconMap.simple.emphasised }}
                    />
                </div>
            )}
        </CoreLayoutView>
    );
};
